import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import type { ContentBlock } from "@/agents/tools/types";
import { ToolOutputRenderer } from "@/components/tool-output-renderer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SearchInput } from "@/components/ui/composed/search-input";
import { Empty, EmptyDescription } from "@/components/ui/empty";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { adminThreadsInfiniteQueryOptions } from "@/hooks/query/thread";
import { useDebounce } from "@/hooks/use-debounce";
import * as m from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { $updateThreadFeatured } from "@/server/actions/thread";

export const Route = createFileRoute("/admin/threads")({
	component: RouteComponent,
});

function RouteComponent() {
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 300);

	const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery(
		adminThreadsInfiniteQueryOptions({
			pageSize: 20,
			search: debouncedSearch || undefined,
		}),
	);

	const threads = data?.pages.flatMap((page) => page.data) ?? [];

	return (
		<div className="container mx-auto py-6 px-4 ">
			<Card className="relative">
				<CardHeader>
					<CardTitle>{m.adminThreads_title()}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4 px-8">
					{/* Search */}
					<div className="sticky top-0 z-10 bg-background">
						<SearchInput
							wrapperClassName="max-w-sm"
							placeholder={m.adminThreads_searchPlaceholder()}
							value={searchInput}
							onChange={(e) => setSearchInput(e.target.value)}
						/>
					</div>
					{/* Table */}
					{isLoading ? (
						<div className="py-8 text-center text-muted-foreground">
							{m.common_loading()}
						</div>
					) : threads.length > 0 ? (
						<Table className="table-fixed">
							<TableHeader>
								<TableRow>
									<TableHead className="w-[300px]">
										{m.adminThreads_colTitle()}
									</TableHead>
									<TableHead>{m.adminThreads_colOutput()}</TableHead>
									<TableHead className="w-[160px]">
										{m.adminThreads_colCreatedAt()}
									</TableHead>
									<TableHead className="w-[100px] text-center">
										{m.adminThreads_colFeatured()}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{threads.map((thread) => (
									<ThreadRow key={thread.id} thread={thread} />
								))}
							</TableBody>
						</Table>
					) : (
						<Empty>
							<EmptyDescription>
								{debouncedSearch
									? m.adminThreads_noMatch()
									: m.adminThreads_empty()}
							</EmptyDescription>
						</Empty>
					)}

					{/* Load more */}
					{hasNextPage && (
						<div className="flex justify-center pt-4">
							<Button
								variant="outline"
								onClick={() => fetchNextPage()}
								disabled={isFetchingNextPage}
							>
								{isFetchingNextPage ? m.common_loading() : m.common_loadMore()}
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

type ThreadRowProps = {
	thread: {
		id: string;
		title: string | null;
		output: ContentBlock[] | null;
		featured: boolean;
		createdAt: Date;
	};
};

function ThreadRow({ thread }: ThreadRowProps) {
	const { mutate: updateFeatured, isPending } = useMutation({
		mutationKey: ["threads", "admin", "updateFeatured"],
		mutationFn: (featured: boolean) =>
			$updateThreadFeatured({ data: { threadId: thread.id, featured } }),
		meta: {
			success: { message: m.common_updateSuccess() },
			error: { message: m.common_updateFailed() },
		},
	});

	const locale = getLocale();

	return (
		<TableRow>
			<TableCell className="max-w-[300px]">
				<div className="flex items-center gap-2">
					<span className="truncate font-medium">
						{thread.title || m.common_unnamedThread()}
					</span>
					{thread.featured && (
						<Badge variant="secondary" className="shrink-0">
							{m.adminThreads_featured()}
						</Badge>
					)}
				</div>
			</TableCell>
			<TableCell>
				{thread.output && thread.output.length > 0 ? (
					<ToolOutputRenderer content={thread.output} className="p-0" />
				) : (
					<span className="text-muted-foreground">{m.adminThreads_noOutput()}</span>
				)}
			</TableCell>
			<TableCell className="text-muted-foreground">
				{new Date(thread.createdAt).toLocaleString(locale, {
					year: "numeric",
					month: "numeric",
					day: "numeric",
					hour: "2-digit",
					minute: "2-digit",
				})}
			</TableCell>
			<TableCell className="text-center">
				<Switch
					checked={thread.featured}
					disabled={isPending}
					onCheckedChange={(checked) => updateFeatured(checked)}
				/>
			</TableCell>
		</TableRow>
	);
}
